import { CurveType } from "./interface";
import { Cartesian2, Cartesian3 } from "cesium";
export class QuadraticBezierSegment {
    type = CurveType.QUADRATIC_BEZIER;
    _rawPoints;
    _controlPoints;
    constructor() {
        this._rawPoints = [];
        this._controlPoints = [];
    }
    get points() {
        return this._rawPoints;
    }
    get length() {
        let length = 0;
        for (let i = 0; i < this._rawPoints.length - 1; i++) {
            length += Cartesian3.distance(this._rawPoints[i], this._rawPoints[i + 1]);
        }
        return length;
    }
    get length_2D() {
        let length = 0;
        for (let i = 0; i < this._rawPoints.length - 1; i++) {
            length += Cartesian2.distance(this._rawPoints[i], this._rawPoints[i + 1]);
        }
        return length;
    }
    /**
     * Function return a point on quadratic bezier curve
     * Refer to this link for more information on quadratic bezier curve: https://en.wikipedia.org/wiki/B%C3%A9zier_curve
     * @param A First point of curve
     * @param B Mid/ControlPoint of curve
     * @param C Last point of curver
     * @param t 0 <= t <= 1 (At what division you need point on bezier curve ex. t = 0.5 will give point on at mid on terrain.)
     * @returns return point on bezier curve
     */
    getQuadraticBezierPointAtParameterT(A, B, C, t) {
        const a1 = Math.pow(1 - t, 2);
        const a2 = (1 - t) * 2 * t;
        const a3 = t * t;
        // P = (Math.pow(1 - t, 2))A + ((1 - t) * 2 * t)B + (t * t)C
        const point = Cartesian3.add(Cartesian3.multiplyByScalar(A, a1, new Cartesian3()), Cartesian3.multiplyByScalar(B, a2, new Cartesian3()), new Cartesian3());
        Cartesian3.add(point, Cartesian3.multiplyByScalar(C, a3, new Cartesian3()), point);
        return point;
    }
    /**
     * Function to get bezier points
     * @param points Array of control points (3 points only as we are drawing qudratic curve)
     * @param divisions Division required for bezier curve
     * @param start: Start of the bezier curve (range 0.0 to 1.0)
     * @param end: End of the bezier curve (range 0.0 to 1.0)
     * @returns Array of bezier curve points
     */
    computeQuadraticBezierCurve(startPoint, controlPoint, endPoint, divisions, start, end) {
        const points = [startPoint, controlPoint, endPoint];
        const bezierPointsArry = [];
        const midCartesianPointsArry = points;
        // for (let iter = 0; iter < points.length - 1; iter++) {
        // 	midCartesianPointsArry.push(
        // 		new Cartesian3(
        // 			(points[iter].x + points[iter + 1].x) / 2,
        // 			(points[iter].y + points[iter + 1].y) / 2,
        // 			(points[iter].z + points[iter + 1].z) / 2,
        // 		),
        // 	);
        // 	if (iter + 1 !== points.length - 1) {
        // 		midCartesianPointsArry.push(
        // 			new Cartesian3(
        // 				points[iter + 1].x,
        // 				points[iter + 1].y,
        // 				points[iter + 1].z,
        // 			),
        // 		);
        // 	}
        // }
        //given 3 points in 3D write an function to find center and radius of circle
        // It is possible that Interpolation pitch is greater than that of the arcLength in this case division = arclen/interpolationpitch = 0
        // In this case path array for the segment is midpointCartesian array and projected point index should be 1 becuase it is ref point of current segment
        if (divisions === 0) {
            bezierPointsArry.push(midCartesianPointsArry[0], midCartesianPointsArry[1], midCartesianPointsArry[2]);
        }
        else if (divisions === 1) {
            const P = this.getQuadraticBezierPointAtParameterT(midCartesianPointsArry[0], midCartesianPointsArry[1], midCartesianPointsArry[2], 0.5);
            bezierPointsArry.push(midCartesianPointsArry[0], P, midCartesianPointsArry[2]);
        }
        else {
            for (let iter = 0; iter + 2 < midCartesianPointsArry.length; iter += 2) {
                for (let t = start; t <= end; t += 1 / divisions) {
                    const P = this.getQuadraticBezierPointAtParameterT(midCartesianPointsArry[iter], midCartesianPointsArry[iter + 1], midCartesianPointsArry[iter + 2], t);
                    bezierPointsArry.push(P);
                }
            }
        }
        return bezierPointsArry;
    }
    generateQuadraticBezierSegment(points) {
        if (points.length < 2) {
            throw new Error("At least 2 points are required to generate quadratic bezier segment");
        }
        this._controlPoints = points;
        if (points.length === 2) {
            this._rawPoints = [points[0], points[1]];
            return;
        }
        const interpolationPitch = 1;
        this._rawPoints = [];
        for (let i = 1; i < points.length - 1; ++i) {
            const startPoint = i - 1 === 0
                ? points[i - 1]
                : Cartesian3.multiplyByScalar(Cartesian3.add(points[i - 1], points[i], new Cartesian3()), 0.5, new Cartesian3());
            const controlPoint = points[i];
            const endPoint = i + 1 === points.length - 1
                ? points[i + 1]
                : Cartesian3.multiplyByScalar(Cartesian3.add(points[i], points[i + 1], new Cartesian3()), 0.5, new Cartesian3());
            const arcLength = Cartesian3.distance(startPoint, controlPoint) +
                Cartesian3.distance(controlPoint, endPoint);
            const divisions = 2 * Math.floor(arcLength / interpolationPitch);
            this._rawPoints.push(...this.computeQuadraticBezierCurve(startPoint, controlPoint, endPoint, divisions, 0, 1));
        }
    }
    getCurveData() {
        return {
            type: CurveType.QUADRATIC_BEZIER,
            points: this._controlPoints,
        };
    }
    dispose() {
        this._rawPoints = [];
        this._controlPoints = [];
    }
    static fromData({ points }) {
        const segment = new QuadraticBezierSegment();
        segment.generateQuadraticBezierSegment(points);
        return segment;
    }
}
//# sourceMappingURL=quadratic-bezier.js.map